/**
 * Setting a validation status (WMC-565) — the `validation` bucket of a
 * bulk-edit request (bulk-editing contract §3.4.5, service contract 4.7.1).
 *
 * One request sets one status on every recording it names, so this is a
 * much smaller cousin of `applyBulkEdits`: no builder rows, no conflicts, no
 * release lookup. The recordings travel with the `validation_id` the backend
 * read off each document (contract 4.7.2), sent back unchanged. Every
 * recording needs one: the backend rejects a request where any is missing,
 * so a selection with such a recording is refused here, before anything is
 * sent (bulk contract §3.4.5).
 *
 * The response is read per action, like the other buckets: the `validation`
 * row's status is the verdict for the whole set. `not_implemented` is a real
 * answer here — the backend dispatches the bucket, but the write belongs to
 * the data platform and is not landed yet — and it means nothing was saved,
 * so it is reported separately from success and from failure.
 */

import type {
  BulkEditRequestBulkEdit,
  PortalApi,
} from 'shared_components/generated/admin';
import { friendlySaveError } from '../saveErrorMessage';

/** Statuses an admin may set. `validated` / `pending` are system-owned. */
export type AdminValidationStatus = 'review' | 'approved' | 'rejected';

/** One recording the status is set on. */
export interface ValidationTarget {
  recordingId: string;
  documentId?: string;
  /** The `id` of the recording's current `validation_state`, or null when
   *  its document carries none yet. A null here means the recording cannot
   *  be validated: `applyValidationStatus` refuses the whole request. */
  validationId: string | null;
}

export type ValidationApplyOutcome =
  | { kind: 'applied' }
  /** The request passed and nothing was written: the data platform has not
   *  implemented the write. Every status is unchanged. */
  | { kind: 'not_implemented' }
  | { kind: 'failed'; message: string };

export function buildValidationPlan(
  targets: ValidationTarget[],
  status: AdminValidationStatus
): BulkEditRequestBulkEdit {
  return {
    recordings: targets.map((t) => ({
      recordingId: t.recordingId,
      documentId: t.documentId,
      validationId: t.validationId,
    })),
    validation: { status },
  };
}

const NO_ROW_MESSAGE = 'The server did not report on the validation status.';
const FAILED_MESSAGE = 'The validation status could not be saved.';

/** Targets whose document carries no validation id, so the backend would
 *  reject the request (bulk contract §3.4.5). */
export function targetsWithoutValidationId(
  targets: ValidationTarget[]
): ValidationTarget[] {
  return targets.filter((t) => !t.validationId);
}

export function missingValidationIdMessage(count: number): string {
  const noun = count === 1 ? 'recording has' : 'recordings have';
  return `${count} selected ${noun} no validation id yet, so the status cannot be set. Nothing was saved.`;
}

/**
 * Send one status for `targets` and read the `validation` row back. Never
 * throws: a request that produced no result comes back as `failed` with a
 * message the UI can show. A target without a `validationId` fails the
 * whole set before any request is made.
 */
export async function applyValidationStatus(
  api: PortalApi,
  userId: string,
  targets: ValidationTarget[],
  status: AdminValidationStatus
): Promise<ValidationApplyOutcome> {
  if (targets.length === 0) {
    return { kind: 'failed', message: 'No recordings selected.' };
  }
  const withoutId = targetsWithoutValidationId(targets);
  if (withoutId.length > 0) {
    return {
      kind: 'failed',
      message: missingValidationIdMessage(withoutId.length),
    };
  }
  try {
    const response = await api.bulkEditBulkEditResultResponse({
      userId,
      bulkEditRequest: {
        bulkEdit: buildValidationPlan(targets, status),
        eventTime: new Date().toISOString(),
      },
    });
    const row = (response.results ?? []).find((r) => r.action === 'validation');
    if (!row) return { kind: 'failed', message: NO_ROW_MESSAGE };
    if (row.status === 'not_implemented') return { kind: 'not_implemented' };
    if (row.status === 'succeeded') return { kind: 'applied' };
    return { kind: 'failed', message: row.error?.message || FAILED_MESSAGE };
  } catch (err) {
    return {
      kind: 'failed',
      message: await friendlySaveError(err, 'recording'),
    };
  }
}
